import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Polygon, Circle, Popup, FeatureGroup, LayersControl, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { EditControl } from "react-leaflet-draw";
import axios from "axios";
import L from "leaflet";
import {
  FaCloudRain,
  FaTemperatureHigh,
  FaTint,
  FaMapMarkerAlt,
  FaTree,
} from "react-icons/fa";
import { XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";
import Detailspdf from './Detailspdf';
import './GreeneryAnalysis.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const COLORS = ['#28a745', '#82ca9d', '#0088FE', '#FFBB28', '#FF8042'];

const GreeneryAnalysisPage = () => {
  const [location, setLocation] = useState("");
  const [pincode, setPincode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [zoomLevel, setZoomLevel] = useState(5);
  const [rectangleBounds, setRectangleBounds] = useState(null);
  const [showSteps, setShowSteps] = useState(true);
  const [greeneryData, setGreeneryData] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [hasDrawnShape, setHasDrawnShape] = useState(false);

  const featureGroupRef = useRef(null);
  const mapRef = useRef(null);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setLatitude(lat.toString());
        setLongitude(lon.toString());
        setLocationPermissionDenied(false);
        fetchAnalysis({ latitude: lat, longitude: lon });
      },
      (err) => {
        console.error("Error getting location:", err);
        setLocationPermissionDenied(true);
        setError("Location permission denied or unavailable");
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  };

  useEffect(() => {
    getUserLocation();
  }, []);

  const handleInputChange = (e) => setLocation(e.target.value);
  const handlePincodeChange = (e) => setPincode(e.target.value);
  const handleLatitudeChange = (e) => setLatitude(e.target.value);
  const handleLongitudeChange = (e) => setLongitude(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postData = {
      location: location || undefined,
      pincode: pincode || undefined,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
    };
    await fetchAnalysis(postData);
  };

  const fetchAnalysis = async (postData) => {
  setLoading(true);
  setError(null);
  setGreeneryData(null);
  try {
    console.log("Sending API request with:", postData);
    const res = await axios.post(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/analyze`, postData, {
      timeout: 80000,
    });
    console.log("API Response:", res.data);

    // Use greenery data directly from the response
    const greeneryDataFromResponse = {
      vegetation_area_hectares: res.data.vegetation_area_hectares ?? null,
      greenery_percentage: res.data.greenery_percentage ?? null,
      estimated_tree_count: res.data.estimated_tree_count ?? null,
      ndvi_mean: res.data.ndvi_mean ?? null,
      vegetation_health: res.data.vegetation_health || "Not Available",
      historical_data: res.data.historical_data || []
    };

    setGreeneryData(greeneryDataFromResponse);
    setData(res.data);

    if (postData.latitude && postData.longitude) {
      setMapCenter([postData.latitude, postData.longitude]);
      setZoomLevel(15);
    } else if ((res.data.latitude && res.data.longitude) || (res.data.lat && res.data.lon)) {
      const lat = res.data.latitude ?? res.data.lat;
      const lon = res.data.longitude ?? res.data.lon;
      setMapCenter([lat, lon]);
      setZoomLevel(res.data.location_type === "district" ? 9 : 7);
    }
  } catch (error) {
    console.error("Error fetching analysis:", error);
    setError("❌ Time Out/Failed to analyze location. Please try again.");
  }
  setLoading(false);
};

  const getConclusionSummary = (greeneryData, locationType) => {
    const { vegetation_area_hectares, greenery_percentage, estimated_tree_count, ndvi_mean, vegetation_health } = greeneryData || {};
    const summary = [];

    summary.push("Based on the greenery analysis:");

    if (locationType === "urban" || locationType === "semi-urban") {
      summary.push("🏙️ This area is classified as urban or semi-urban. Green spaces may be limited due to built-up infrastructure.");
      summary.push("Consider urban forestry initiatives, rooftop gardens, or community green spaces.");
      return summary.join("\n");
    }

    if (greenery_percentage != null) {
      if (greenery_percentage > 70) {
        summary.push(`🌳 Greenery Coverage: Excellent vegetation coverage with ${greenery_percentage}% green areas.`);
      } else if (greenery_percentage > 40) {
        summary.push(`🌿 Greenery Coverage: Moderate vegetation coverage with ${greenery_percentage}% green areas.`);
      } else {
        summary.push(`🌱 Greenery Coverage: Limited vegetation coverage with ${greenery_percentage}% green areas.`);
      }
    }

    if (estimated_tree_count != null) {
      summary.push(`🌲 Estimated Tree Count: Approximately ${estimated_tree_count} trees.`);
    }

    if (vegetation_area_hectares != null) {
      summary.push(`📏 Vegetation Area: ${vegetation_area_hectares} hectares of vegetation cover detected.`);
    }

    if (ndvi_mean != null) {
      if (ndvi_mean > 0.7) {
        summary.push(`🌿 Vegetation Health: Excellent plant health with NDVI of ${ndvi_mean.toFixed(3)}.`);
      } else if (ndvi_mean > 0.4) {
        summary.push(`🌱 Vegetation Health: Good plant health with NDVI of ${ndvi_mean.toFixed(3)}.`);
      } else {
        summary.push(`🍂 Vegetation Health: Poor plant health with NDVI of ${ndvi_mean.toFixed(3)}.`);
      }
    }

    if (vegetation_health) {
      summary.push(`🌟 Overall Vegetation Health: Rated as ${vegetation_health}.`);
    }

    return summary.join("\n");
  };

  const getPolygonPositions = () => {
    if (!data?.polygon) return [];
    return data.polygon.map((coord) => [coord[1], coord[0]]);
  };

  const getPolygonColor = () => {
    return { color: "#28a745", fillColor: "#28a745", fillOpacity: 0.2 };
  };

  const _onCreated = async (e) => {
    const layer = e.layer;
    if (layer && layer.getBounds) {
      const bounds = layer.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const newRectangleBounds = [
        [sw.lat, sw.lng],
        [ne.lat, ne.lng]
      ];
      setRectangleBounds(newRectangleBounds);
      setHasDrawnShape(true);

      const lat = (sw.lat + ne.lat) / 2;
      const lon = (sw.lng + ne.lng) / 2;

      const postData = {
        latitude: lat,
        longitude: lon,
        rectangle: newRectangleBounds,
      };
      await fetchAnalysis(postData);

      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
      }
    }
  };

  const _onDeleted = () => {
    setRectangleBounds(null);
    setData(null);
    setGreeneryData(null);
    setError(null);
    setHasDrawnShape(false);
    if (mapRef.current) {
      setMapCenter([20.5937, 78.9629]);
      setZoomLevel(5);
      mapRef.current.setView([20.5937, 78.9629], 5);
    }
  };

  const calculateArea = (bounds) => {
    const R = 6371; // Earth's radius in km
    const lat1 = bounds[0][0] * (Math.PI / 180);
    const lat2 = bounds[1][0] * (Math.PI / 180);
    const lon1 = bounds[0][1] * (Math.PI / 180);
    const lon2 = bounds[1][1] * (Math.PI / 180);

    const dLat = Math.abs(lat2 - lat1);
    const dLon = Math.abs(lon2 - lon1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceLat = R * dLat;
    const distanceLon = R * dLon * Math.cos((lat1 + lat2) / 2);

    const area = distanceLat * distanceLon;
    return area.toFixed(2);
  };

  useEffect(() => {
    if (mapRef.current && mapCenter && zoomLevel) {
      mapRef.current.setView(mapCenter, zoomLevel);
    }
  }, [mapCenter, zoomLevel]);

  return (
    <div className="india-map-container">
      <div className="header-section">
        <h1 className="header-title">
          <FaTree /> Indian Greenery Analysis
        </h1>
        <p className="header-subtitle">
          Get <strong>real-time vegetation and greenery analysis</strong> based on your state, district, or custom area. Monitor forest cover, tree count, and vegetation health.
        </p>
      </div>

      <div className="steps-section" onClick={() => setShowSteps(!showSteps)}>
        <h3 className="steps-title">How It Works</h3>
        {showSteps && (
          <ol className="steps-list">
            <li>Enter a State, District, or Latitude/Longitude.</li>
            <li>For detailed analysis, draw a rectangle on the map.</li>
            <li>The system fetches real-time vegetation data for the selected area.</li>
            <li>Data is analyzed to provide greenery insights and tree estimates.</li>
            <li>View detailed vegetation analysis and visualize the area on the map.</li>
          </ol>
        )}
      </div>

      <div className="input-section">
        <h3 className="input-title">Enter a State, District, or Latitude/Longitude</h3>
        <div className="input-form">
          <input
            type="text"
            value={location}
            onChange={handleInputChange}
            placeholder="State (e.g., Punjab, Ludhiana)"
            className="input-field location-input"
            onFocus={(e) => e.target.classList.add("focused")}
            onBlur={(e) => e.target.classList.remove("focused")}
          />
          <input
            type="text"
            value={pincode}
            onChange={handlePincodeChange}
            placeholder="Pincode (e.g., 110001)"
            className="input-field pincode-input"
            onFocus={(e) => e.target.classList.add("focused")}
            onBlur={(e) => e.target.classList.remove("focused")}
          />
          <input
            type="text"
            value={latitude}
            onChange={handleLatitudeChange}
            placeholder="Latitude (e.g., 28.5388)"
            className="input-field latitude-input"
            onFocus={(e) => e.target.classList.add("focused")}
            onBlur={(e) => e.target.classList.remove("focused")}
          />
          <input
            type="text"
            value={longitude}
            onChange={handleLongitudeChange}
            placeholder="Longitude (e.g., 77.1551)"
            className="input-field longitude-input"
            onFocus={(e) => e.target.classList.add("focused")}
            onBlur={(e) => e.target.classList.remove("focused")}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`submit-button ${loading ? "disabled" : ""}`}
          >
            {loading ? "🔄 Analyzing..." : "Analyze Greenery"}
          </button>
        </div>
        {error && (
          <div className="error-message">{error}</div>
        )}
      </div>

      <div className="map-section">
        <h3 className="map-title">🗺 Location Map</h3>
        <MapContainer
          center={mapCenter}
          zoom={zoomLevel}
          className="map-container"
          ref={mapRef}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Satellite View">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={28}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Street Map">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={18}
              />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay checked name="Place Labels">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution='© <a href="https://www.esri.com/">Esri</a>'
                maxZoom={18}
              />
            </LayersControl.Overlay>
          </LayersControl>
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topright"
              onCreated={_onCreated}
              onDeleted={_onDeleted}
              draw={{
                rectangle: {
                  shapeOptions: {
                    color: "#28a745",
                    weight: 2,
                    fillOpacity: 0.2
                  }
                },
                polygon: {
                  allowIntersection: false,
                  shapeOptions: {
                    color: "#28a745",
                    weight: 2,
                    fillOpacity: 0.2
                  }
                },
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
              }}
              edit={{
                edit: false,
                remove: true,
              }}
            />
            {(latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) ? (
              <Marker position={[parseFloat(latitude), parseFloat(longitude)]}>
                <Popup>
                  <strong>{data?.location || location || "Entered Location"}</strong><br />
                  📍 {parseFloat(latitude).toFixed(4)}, {parseFloat(longitude).toFixed(4)}
                </Popup>
              </Marker>
            ) : (
              data && (data.latitude || data.lat) && (data.longitude || data.lon) && (
                <Marker position={[data.latitude || data.lat, data.longitude || data.lon]}>
                  <Popup>
                    <strong>{data.location || location}</strong><br />
                    📍 {(data.latitude || data.lat).toFixed(4)}, {(data.longitude || data.lon).toFixed(4)}
                  </Popup>
                </Marker>
              )
            )}
          </FeatureGroup>
          {data && (data.latitude || data.lat) && (data.longitude || data.lon) && (
            <>
              <Circle
                center={[data.latitude || data.lat, data.longitude || data.lon]}
                // radius={500}
                // pathOptions={{ color: '#28a745', fillOpacity: 0.2 }}
              >
                <Popup>
                  <strong>{data.location || location}</strong><br />
                  📍 {(data.latitude || data.lat).toFixed(4)}, {(data.longitude || data.lon).toFixed(4)}
                </Popup>
              </Circle>
              {data.polygon && (
                <Polygon positions={getPolygonPositions()} pathOptions={getPolygonColor()} />
              )}
            </>
          )}
        </MapContainer>
        {rectangleBounds && (
          <div className="area-info">
            Total Area: {calculateArea(rectangleBounds)} sq. km
          </div>
        )}
      </div>

      <div className="data-section">
        {loading && (
          <div className="loading-message">
            <p>🔄 Analyzing greenery...</p>
          </div>
        )}
        {data && !loading && (
          <>
            <div data-section="location-details" className="data-card">
              <h3 className="card-title">📍 Location Details</h3>
              <p><FaMapMarkerAlt color="#28a745" /> <strong>Location:</strong> {data.location || data.location_name || location || "N/A"}</p>
              <p><strong>Type:</strong>
                <span className={`type-badge ${data.location_type || ''}`}>
                  {data.location_type || "N/A"}
                </span>
              </p>
              {data.state && <p><strong>State:</strong> {data.state}</p>}
              <p><strong>Coordinates:</strong> {(data.latitude || data.lat)?.toFixed(4) || "N/A"}, {(data.longitude || data.lon)?.toFixed(4) || "N/A"}</p>
            </div>

            <div data-section="weather-data" className="data-card">
              <h3 className="card-title">🌤 Weather Data</h3>
              <p><FaCloudRain color="#17a2b8" /> <strong>Rainfall:</strong> {data.rainfall_mm != null ? `${data.rainfall_mm} mm` : "N/A"}</p>
              <p><FaTemperatureHigh color="#fd7e14" /> <strong>Temperature:</strong> {data.temperature_c != null ? `${data.temperature_c}°C` : "N/A"}</p>
              <p><FaTint color="#6f42c1" /> <strong>Soil Moisture:</strong> {data.soil_moisture != null ? `${(data.soil_moisture * 100).toFixed(1)}%` : "N/A"}</p>
            </div>

            {!hasDrawnShape && (
              <div className="draw-shape-message">
                Please draw a shape on the map to view detailed greenery analysis including vegetation coverage, tree count, and historical data.
              </div>
            )}

            {hasDrawnShape && greeneryData && (
              <>
                <div data-section="greenery-analysis" className="data-card">
                  <h3 className="card-title">🌳 Greenery Analysis</h3>
                  <table className="greenery-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Total Area (sq. km)</th>
                        <th>Vegetation Area (ha)</th>
                        <th>Greenery Percentage (%)</th>
                        <th>Estimated Tree Count</th>
                        <th>NDVI Mean</th>
                        <th>Vegetation Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Current</td>
                        <td>{rectangleBounds ? calculateArea(rectangleBounds) : "N/A"}</td>
                        <td>{greeneryData.vegetation_area_hectares != null ? greeneryData.vegetation_area_hectares.toFixed(2) : "N/A"}</td>
                        <td>{greeneryData.greenery_percentage != null ? greeneryData.greenery_percentage.toFixed(1) : "N/A"}</td>
                        <td>{greeneryData.estimated_tree_count != null ? greeneryData.estimated_tree_count : "N/A"}</td>
                        <td>{greeneryData.ndvi_mean != null ? greeneryData.ndvi_mean.toFixed(3) : "N/A"}</td>
                        <td>{greeneryData.vegetation_health || "N/A"}</td>
                      </tr>
                      {greeneryData.historical_data && greeneryData.historical_data.length > 0 ? (
                        greeneryData.historical_data.map((entry, index) => (
                          <tr key={index}>
                            <td>{entry.year || "N/A"}</td>
                            <td>{rectangleBounds ? calculateArea(rectangleBounds) : "N/A"}</td>
                            <td>{entry.vegetation_area_hectares != null ? entry.vegetation_area_hectares.toFixed(2) : "N/A"}</td>
                            <td>{entry.greenery_percentage != null ? entry.greenery_percentage.toFixed(1) : "N/A"}</td>
                            <td>{entry.estimated_tree_count != null ? entry.estimated_tree_count : "N/A"}</td>
                            <td>{entry.ndvi_mean != null ? entry.ndvi_mean.toFixed(3) : "N/A"}</td>
                            <td>{entry.vegetation_health || "N/A"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7">No historical data available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div data-section="conclusion" className="data-card">
                  <h3 className="card-title">📝 Conclusion & Recommendations</h3>
                  <p>{getConclusionSummary(greeneryData, data?.location_type)}</p>
                </div>

                {greeneryData.historical_data && greeneryData.historical_data.length > 0 && (
                  <div data-section="historical-data" className="data-card">
                    <h3 className="card-title">📈 Historical Data (Last 5 Years)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={greeneryData.historical_data}>
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="vegetation_area_hectares" stroke="#28a745" name="Vegetation Area (ha)" />
                        <Line type="monotone" dataKey="greenery_percentage" stroke="#82ca9d" name="Greenery Percentage" />
                        <Line type="monotone" dataKey="ndvi_mean" stroke="#0088FE" name="NDVI Mean" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="pdf-export-section">
                  <Detailspdf 
                    data={data} 
                    greeneryData={greeneryData} 
                    hasDrawnShape={hasDrawnShape} 
                    rectangleBounds={rectangleBounds} 
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GreeneryAnalysisPage;
